import { useEffect, useMemo, useState } from "react";
import { parseHookEnabled } from "../../../lib/formBuilder/submitHooks";
import { IconPlus, IconTrash } from "../../../components/icons";
import { InlineEditable } from "../../../components/inline/InlineEditable";
import {
  Checkbox,
  FieldLabel,
  SelectInput,
  SwitchControl,
  TextArea,
  TextInput,
} from "../_components/AdminShell";
import { IconButton } from "../_components/IconButton";
import { BodyTemplateEditor, type FormFieldOption } from "./BodyTemplateEditor";

export type { FormFieldOption };

type HookMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type HookFormat = "json" | "form" | "query";
type HookMode = "template" | "mapping";

type HookRetry = {
  enabled?: boolean;
  maxAttempts?: number;
  delayMs?: number;
};

type SubmitHookDraft = {
  url: string;
  enabled?: boolean;
  method?: HookMethod;
  format?: HookFormat;
  mode?: HookMode;
  bodyTemplate?: string;
  fieldMapping?: Record<string, string>;
  fioSplit?: Record<string, unknown>;
  headers?: Record<string, string>;
  sendDeliveryId?: boolean;
  timeoutSeconds?: number;
  retry?: HookRetry;
};

type KeyValueRow = {
  clientId: string;
  key: string;
  value: string;
};

let rowIdSeq = 0;

function nextRowId(): string {
  rowIdSeq += 1;
  return `row-${rowIdSeq}`;
}

function normalizeHookDraft(raw: Record<string, unknown>): SubmitHookDraft {
  const pickString = (...keys: string[]) => {
    for (const key of keys) {
      const value = raw[key];
      if (typeof value === "string") return value;
    }
    return undefined;
  };
  const pickRecord = (...keys: string[]) => {
    for (const key of keys) {
      const value = raw[key];
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as Record<string, unknown>;
      }
    }
    return undefined;
  };
  const pickBoolean = (...keys: string[]) => {
    for (const key of keys) {
      const value = raw[key];
      if (typeof value === "boolean") return value;
    }
    return undefined;
  };
  const pickNumber = (...keys: string[]) => {
    for (const key of keys) {
      const value = raw[key];
      if (typeof value === "number" && Number.isFinite(value)) return value;
    }
    return undefined;
  };

  return {
    url: pickString("url") ?? "",
    enabled: parseHookEnabled(raw),
    method: pickString("method") as HookMethod | undefined,
    format: pickString("format") as HookFormat | undefined,
    mode: pickString("mode") as HookMode | undefined,
    bodyTemplate: pickString("bodyTemplate", "body_template"),
    fieldMapping: (pickRecord("fieldMapping", "field_mapping") ?? {}) as Record<
      string,
      string
    >,
    fioSplit: pickRecord("fioSplit", "fio_split"),
    headers: pickRecord("headers") as Record<string, string> | undefined,
    sendDeliveryId: pickBoolean("sendDeliveryId", "send_delivery_id"),
    timeoutSeconds: pickNumber("timeoutSeconds", "timeout_seconds"),
    retry: raw.retry as HookRetry | undefined,
  };
}

function fioFieldError(
  fieldName: string,
  fioFieldNames: string[],
  formFields: FormFieldOption[],
): string | null {
  const name = fieldName.trim();
  if (!name) return "Укажите поле формы";
  if (fioFieldNames.includes(name)) return null;
  if (formFields.some((field) => field.name === name)) {
    return `Поле «${name}» есть в форме, но не является полем типа ФИО`;
  }
  return `Поле «${name}» не найдено в форме`;
}

function safeParseHooks(raw: string): {
  hooks: SubmitHookDraft[];
  error: string | null;
} {
  try {
    const parsed = JSON.parse(raw || "[]") as unknown;
    const arr = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === "object"
        ? [parsed]
        : [];
    const hooks = arr
      .filter((item) => item && typeof item === "object")
      .map((item) => normalizeHookDraft(item as Record<string, unknown>));
    return { hooks, error: null };
  } catch {
    return { hooks: [], error: "Некорректный JSON" };
  }
}

function normalizeMappingText(mapping?: Record<string, string>): string {
  const entries = Object.entries(mapping ?? {});
  if (entries.length === 0) return "";
  return entries.map(([from, to]) => `${from}: ${to}`).join("\n");
}

function parseMappingText(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  for (const line of lines) {
    const idx = line.indexOf(":");
    if (idx <= 0) continue;
    const from = line.slice(0, idx).trim();
    const to = line.slice(idx + 1).trim();
    if (!from || !to) continue;
    out[from] = to;
  }
  return out;
}

function rowsFromRecord(record?: Record<string, string>): KeyValueRow[] {
  const entries = Object.entries(record ?? {});
  if (entries.length === 0) {
    return [{ clientId: nextRowId(), key: "", value: "" }];
  }
  return entries.map(([key, value]) => ({
    clientId: nextRowId(),
    key,
    value,
  }));
}

function recordFromRows(rows: KeyValueRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    out[key] = row.value;
  }
  return out;
}

type FioSplitParts = {
  last_name: string;
  first_name: string;
  patronymic: string;
};

type FioSplitRuleDraft = {
  clientId: string;
  fieldName: string;
  splitBySpace: boolean;
  parts: FioSplitParts;
};

const FIO_PART_META: Array<{
  key: keyof FioSplitParts;
  label: string;
  placeholder: string;
}> = [
  { key: "last_name", label: "Фамилия", placeholder: "lastname" },
  { key: "first_name", label: "Имя", placeholder: "name" },
  { key: "patronymic", label: "Отчество", placeholder: "fathername" },
];

function defaultFioParts(): FioSplitParts {
  return {
    last_name: "lastname",
    first_name: "name",
    patronymic: "fathername",
  };
}

function createEmptyFioRule(fieldName = "fio"): FioSplitRuleDraft {
  return {
    clientId: nextRowId(),
    fieldName,
    splitBySpace: false,
    parts: defaultFioParts(),
  };
}

function rulesFromFioSplit(raw?: Record<string, unknown>): FioSplitRuleDraft[] {
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw).map(([fieldName, config]) => {
    const c =
      config && typeof config === "object"
        ? (config as Record<string, unknown>)
        : {};
    const parts =
      c.parts && typeof c.parts === "object"
        ? (c.parts as Record<string, string>)
        : {};
    return {
      clientId: nextRowId(),
      fieldName,
      splitBySpace: Boolean(c.splitBySpace),
      parts: {
        last_name: parts.last_name ?? "lastname",
        first_name: parts.first_name ?? "name",
        patronymic: parts.patronymic ?? "fathername",
      },
    };
  });
}

function fioSplitFromRules(
  rules: FioSplitRuleDraft[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const rule of rules) {
    const fieldName = rule.fieldName.trim();
    if (!fieldName) continue;
    out[fieldName] = {
      splitBySpace: rule.splitBySpace,
      parts: { ...rule.parts },
    };
  }
  return out;
}

function FioSplitEditor({
  resetKey,
  fioSplit,
  fioFieldNames,
  formFields,
  onChange,
}: {
  resetKey: string;
  fioSplit?: Record<string, unknown>;
  fioFieldNames: string[];
  formFields: FormFieldOption[];
  onChange: (fioSplit: Record<string, unknown>) => void;
}) {
  const [rules, setRules] = useState(() => rulesFromFioSplit(fioSplit));

  useEffect(() => {
    setRules(rulesFromFioSplit(fioSplit));
  }, [resetKey]);

  const commit = (nextRules: FioSplitRuleDraft[]) => {
    setRules(nextRules);
    onChange(fioSplitFromRules(nextRules));
  };

  const updateRule = (
    clientId: string,
    patch: Partial<Omit<FioSplitRuleDraft, "clientId">>,
  ) => {
    commit(
      rules.map((rule) =>
        rule.clientId === clientId ? { ...rule, ...patch } : rule,
      ),
    );
  };

  const updatePart = (
    clientId: string,
    partKey: keyof FioSplitParts,
    value: string,
  ) => {
    commit(
      rules.map((rule) =>
        rule.clientId === clientId
          ? { ...rule, parts: { ...rule.parts, [partKey]: value } }
          : rule,
      ),
    );
  };

  const addRule = () => {
    const used = new Set(rules.map((rule) => rule.fieldName));
    const defaultName =
      fioFieldNames.find((name) => !used.has(name)) ??
      fioFieldNames[0] ??
      "fio";
    commit([...rules, createEmptyFioRule(defaultName)]);
  };

  const removeRule = (clientId: string) => {
    commit(rules.filter((rule) => rule.clientId !== clientId));
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white/80 p-2.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <FieldLabel size="compact">Разбор ФИО (fioSplit)</FieldLabel>
          <p className="font-onest text-[11px] text-slate-500">
            Части поля ФИО станут переменными для bodyTemplate.
          </p>
        </div>
        <button
          type="button"
          onClick={addRule}
          className="inline-flex shrink-0 items-center gap-1 font-onest text-xs font-medium text-brand-700 hover:text-brand-600"
        >
          <IconPlus size={13} />
          Добавить
        </button>
      </div>

      {rules.length === 0 ? (
        <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-center font-onest text-xs text-slate-500">
          Не настроено
        </p>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => {
            const fieldError = fioFieldError(
              rule.fieldName,
              fioFieldNames,
              formFields,
            );
            return (
              <article
                key={rule.clientId}
                className={[
                  "rounded-md border bg-slate-50/80 p-2.5",
                  fieldError
                    ? "border-red-200 bg-red-50/30"
                    : "border-slate-100",
                ].join(" ")}
              >
                <div className="mb-2 flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <FieldLabel size="compact">Поле формы (name)</FieldLabel>
                    {fioFieldNames.length > 0 ? (
                      <SelectInput
                        size="compact"
                        value={rule.fieldName}
                        onChange={(fieldName) =>
                          updateRule(rule.clientId, { fieldName })
                        }
                      >
                        {fioFieldNames.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                        {!fioFieldNames.includes(rule.fieldName) &&
                        rule.fieldName ? (
                          <option value={rule.fieldName}>
                            {rule.fieldName}
                          </option>
                        ) : null}
                      </SelectInput>
                    ) : (
                      <TextInput
                        size="compact"
                        className="font-mono text-xs"
                        value={rule.fieldName}
                        placeholder="fio"
                        onChange={(fieldName) =>
                          updateRule(rule.clientId, { fieldName })
                        }
                      />
                    )}
                    {fieldError ? (
                      <p className="mt-1 font-onest text-[11px] text-red-600">
                        {fieldError}
                      </p>
                    ) : null}
                  </div>
                  <IconButton
                    title="Удалить правило"
                    size="sm"
                    variant="danger"
                    className="!mt-5 !h-8 !w-8 shrink-0"
                    onClick={() => removeRule(rule.clientId)}
                  >
                    <IconTrash size={13} />
                  </IconButton>
                </div>

                <Checkbox
                  id={`fio-split-space-${rule.clientId}`}
                  size="compact"
                  checked={rule.splitBySpace}
                  onChange={(splitBySpace) =>
                    updateRule(rule.clientId, { splitBySpace })
                  }
                  label="Разбивать строку по пробелам"
                />

                <div className="mt-2 space-y-1.5">
                  <p className="font-onest text-[11px] font-medium text-slate-500">
                    Переменные в шаблоне
                  </p>
                  {FIO_PART_META.map((part) => (
                    <div
                      key={part.key}
                      className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-2"
                    >
                      <span className="font-onest text-xs text-slate-600">
                        {part.label}
                      </span>
                      <TextInput
                        size="compact"
                        className="font-mono text-xs"
                        value={rule.parts[part.key]}
                        placeholder={part.placeholder}
                        onChange={(value) =>
                          updatePart(rule.clientId, part.key, value)
                        }
                      />
                    </div>
                  ))}
                </div>

                <p className="mt-2 font-onest text-[11px] text-slate-400">
                  В bodyTemplate:{" "}
                  <code className="rounded bg-white px-1 py-px">
                    {`{{${rule.parts.first_name}|json}}`}
                  </code>
                  ,{" "}
                  <code className="rounded bg-white px-1 py-px">
                    {`{{${rule.parts.last_name}|json}}`}
                  </code>
                </p>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function createEmptyHook(): SubmitHookDraft {
  return {
    url: "",
    enabled: true,
    method: "POST",
    format: "json",
    mode: "template",
    sendDeliveryId: true,
    timeoutSeconds: 5,
    retry: { enabled: false, maxAttempts: 3, delayMs: 2000 },
    headers: { "Content-Type": "application/json" },
    bodyTemplate: "{\n  \n}",
    fieldMapping: {},
    fioSplit: {},
  };
}

function KeyValueListEditor({
  label,
  rows,
  keyPlaceholder,
  valuePlaceholder,
  onChange,
}: {
  label: string;
  rows: KeyValueRow[];
  keyPlaceholder: string;
  valuePlaceholder: string;
  onChange: (rows: KeyValueRow[]) => void;
}) {
  const updateRow = (clientId: string, patch: Partial<KeyValueRow>) => {
    onChange(
      rows.map((row) =>
        row.clientId === clientId ? { ...row, ...patch } : row,
      ),
    );
  };

  const addRow = () => {
    onChange([...rows, { clientId: nextRowId(), key: "", value: "" }]);
  };

  const removeRow = (clientId: string) => {
    const next = rows.filter((row) => row.clientId !== clientId);
    onChange(
      next.length > 0 ? next : [{ clientId: nextRowId(), key: "", value: "" }],
    );
  };

  return (
    <div>
      <FieldLabel size="compact">{label}</FieldLabel>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.clientId} className="flex items-center gap-1.5">
            <TextInput
              size="compact"
              className="min-w-0 flex-[2] font-mono text-xs"
              value={row.key}
              placeholder={keyPlaceholder}
              onChange={(key) => updateRow(row.clientId, { key })}
            />
            <TextInput
              size="compact"
              className="min-w-0 flex-[3] font-mono text-xs"
              value={row.value}
              placeholder={valuePlaceholder}
              onChange={(value) => updateRow(row.clientId, { value })}
            />
            <IconButton
              title="Удалить"
              size="sm"
              variant="danger"
              className="!h-8 !w-8 shrink-0"
              onClick={() => removeRow(row.clientId)}
            >
              <IconTrash size={13} />
            </IconButton>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        className="mt-1.5 inline-flex items-center gap-1 font-onest text-xs font-medium text-brand-700 hover:text-brand-600"
      >
        <IconPlus size={13} />
        Добавить
      </button>
    </div>
  );
}

function HookHeadersEditor({
  resetKey,
  headers,
  onChange,
}: {
  resetKey: string;
  headers?: Record<string, string>;
  onChange: (headers: Record<string, string>) => void;
}) {
  const [rows, setRows] = useState(() => rowsFromRecord(headers));

  useEffect(() => {
    setRows(rowsFromRecord(headers));
  }, [resetKey]);

  const handleChange = (nextRows: KeyValueRow[]) => {
    setRows(nextRows);
    onChange(recordFromRows(nextRows));
  };

  return (
    <KeyValueListEditor
      label="Заголовки"
      rows={rows}
      keyPlaceholder="Content-Type"
      valuePlaceholder="application/json"
      onChange={handleChange}
    />
  );
}

export function SubmitHooksEditor({
  value,
  onChange,
  formFields = [],
  fioFieldNames = [],
}: {
  value: string;
  onChange: (next: string) => void;
  formFields?: FormFieldOption[];
  fioFieldNames?: string[];
}) {
  const parsed = useMemo(() => safeParseHooks(value), [value]);
  const [hooks, setHooks] = useState<SubmitHookDraft[]>(parsed.hooks);
  const [showJson, setShowJson] = useState(false);

  useEffect(() => {
    setHooks(parsed.hooks);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- synced from JSON source
  }, [value]);

  const commit = (nextHooks: SubmitHookDraft[]) => {
    setHooks(nextHooks);
    onChange(
      JSON.stringify(
        nextHooks.map((hook) => ({
          ...hook,
          enabled: hook.enabled !== false,
        })),
        null,
        2,
      ),
    );
  };

  const updateHook = (idx: number, patch: Partial<SubmitHookDraft>) => {
    commit(hooks.map((hook, i) => (i === idx ? { ...hook, ...patch } : hook)));
  };

  const removeHook = (idx: number) => {
    commit(hooks.filter((_, i) => i !== idx));
  };

  const addHook = () => {
    commit([...hooks, createEmptyHook()]);
  };

  return (
    <section className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-card-lg">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-onest text-sm font-semibold text-ink">
            Submit hooks
          </h2>
          <p className="mt-0.5 font-onest text-[11px] leading-snug text-slate-500">
            Вебхуки отправляются асинхронно после успешной отправки формы.
          </p>
        </div>
        <button
          type="button"
          onClick={addHook}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-onest text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
        >
          <IconPlus size={14} />
          Добавить
        </button>
      </div>

      {parsed.error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="font-onest text-xs text-red-700">
            Submit hooks: {parsed.error}. Исправьте JSON ниже или удалите его.
          </p>
        </div>
      ) : null}

      {hooks.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center font-onest text-xs text-slate-500">
          Хуков пока нет
        </p>
      ) : (
        <div className="space-y-2">
          {hooks.map((hook, idx) => {
            const mappingText = normalizeMappingText(hook.fieldMapping);
            const retry = hook.retry ?? {};
            const mode = hook.mode ?? "template";
            const format = hook.format ?? "json";
            const method = hook.method ?? "POST";
            const enabled = hook.enabled !== false;
            const summaryUrl = hook.url.trim() || "Новый webhook";

            return (
              <details
                key={`hook-${idx}`}
                className={[
                  "group rounded-lg border bg-slate-50/60",
                  enabled
                    ? "border-slate-200"
                    : "border-slate-200/80 opacity-80",
                ].join(" ")}
              >
                <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
                  <span className="shrink-0 text-slate-400 transition group-open:rotate-90">
                    ▸
                  </span>
                  <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-500">
                    {idx + 1}
                  </span>
                  <span
                    className={[
                      "min-w-0 flex-1 truncate font-onest text-sm",
                      enabled ? "font-medium text-ink" : "text-slate-500",
                    ].join(" ")}
                    title={summaryUrl}
                  >
                    {summaryUrl}
                  </span>
                  <span
                    className={[
                      "shrink-0 rounded px-1.5 py-0.5 font-onest text-[10px] font-semibold uppercase tracking-wide",
                      enabled
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-slate-200 text-slate-600",
                    ].join(" ")}
                  >
                    {enabled ? "вкл" : "выкл"}
                  </span>
                  <span
                    className="shrink-0"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <SwitchControl
                      id={`hook-enabled-${idx}`}
                      compact
                      checked={enabled}
                      onChange={(nextEnabled) =>
                        updateHook(idx, { enabled: nextEnabled })
                      }
                      ariaLabel={
                        enabled ? "Выключить webhook" : "Включить webhook"
                      }
                      checkedClassName="bg-emerald-500"
                      uncheckedClassName="bg-slate-300"
                    />
                  </span>
                  <span onClick={(event) => event.stopPropagation()}>
                    <IconButton
                      title="Удалить webhook"
                      size="sm"
                      variant="danger"
                      onClick={() => removeHook(idx)}
                    >
                      <IconTrash size={14} />
                    </IconButton>
                  </span>
                </summary>

                <div className="space-y-2 border-t border-slate-200 px-3 pb-3 pt-2">
                  <div className="flex items-center justify-between gap-2">
                    <InlineEditable
                      value={hook.url}
                      placeholder="https://example.com/webhook"
                      onChange={(url) => updateHook(idx, { url })}
                      className="block min-w-0 flex-1 truncate font-onest text-sm font-medium text-ink"
                      emptyClassName="block min-w-0 truncate font-onest text-sm font-medium text-slate-400"
                      inputClassName="w-full text-sm"
                    />
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3">
                    <div>
                      <FieldLabel size="compact">Метод</FieldLabel>
                      <SelectInput
                        size="compact"
                        value={method}
                        onChange={(next) =>
                          updateHook(idx, { method: next as HookMethod })
                        }
                      >
                        {(
                          ["GET", "POST", "PUT", "PATCH", "DELETE"] as const
                        ).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                    <div>
                      <FieldLabel size="compact">Формат</FieldLabel>
                      <SelectInput
                        size="compact"
                        value={format}
                        onChange={(next) =>
                          updateHook(idx, { format: next as HookFormat })
                        }
                      >
                        {(["json", "form", "query"] as const).map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                    <div>
                      <FieldLabel size="compact">Режим</FieldLabel>
                      <SelectInput
                        size="compact"
                        value={mode}
                        onChange={(next) =>
                          updateHook(idx, { mode: next as HookMode })
                        }
                      >
                        <option value="template">template</option>
                        <option value="mapping">mapping</option>
                      </SelectInput>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <FieldLabel size="compact">Timeout (сек)</FieldLabel>
                      <TextInput
                        size="compact"
                        type="number"
                        value={String(hook.timeoutSeconds ?? 5)}
                        onChange={(v) =>
                          updateHook(idx, { timeoutSeconds: Number(v) || 5 })
                        }
                      />
                    </div>
                    <div className="flex items-end">
                      <Checkbox
                        id={`delivery-${idx}`}
                        size="compact"
                        checked={hook.sendDeliveryId !== false}
                        onChange={(sendDeliveryId) =>
                          updateHook(idx, { sendDeliveryId })
                        }
                        label="X-Delivery-Id"
                      />
                    </div>
                  </div>

                  <HookHeadersEditor
                    resetKey={`${idx}-${hook.url}`}
                    headers={hook.headers}
                    onChange={(headers) => updateHook(idx, { headers })}
                  />

                  {mode === "template" ? (
                    <>
                      <FioSplitEditor
                        resetKey={`${idx}-${hook.url}`}
                        fioSplit={hook.fioSplit}
                        fioFieldNames={fioFieldNames}
                        formFields={formFields}
                        onChange={(fioSplit) => updateHook(idx, { fioSplit })}
                      />
                      <BodyTemplateEditor
                        resetKey={`${idx}-${hook.url}`}
                        value={hook.bodyTemplate ?? ""}
                        formFields={formFields}
                        fioSplit={hook.fioSplit}
                        onChange={(bodyTemplate) =>
                          updateHook(idx, { bodyTemplate })
                        }
                      />
                    </>
                  ) : (
                    <div>
                      <FieldLabel size="compact">
                        fieldMapping (по строкам)
                      </FieldLabel>
                      <TextArea
                        size="compact"
                        value={mappingText}
                        rows={5}
                        placeholder={"fio: participantName\n@formId: formId"}
                        onChange={(text) =>
                          updateHook(idx, {
                            fieldMapping: parseMappingText(text),
                          })
                        }
                      />
                    </div>
                  )}

                  <details className="rounded-md border border-slate-200 bg-white/70">
                    <summary className="cursor-pointer list-none px-2.5 py-1.5 font-onest text-xs text-slate-600 marker:content-none hover:text-slate-800 [&::-webkit-details-marker]:hidden">
                      Retry
                    </summary>
                    <div className="space-y-2 border-t border-slate-200 px-2.5 pb-2.5 pt-2">
                      <div className="grid gap-2 sm:grid-cols-3">
                        <Checkbox
                          id={`retry-enabled-${idx}`}
                          size="compact"
                          checked={Boolean(retry.enabled)}
                          onChange={(enabled) =>
                            updateHook(idx, {
                              retry: { ...retry, enabled },
                            })
                          }
                          label="Retry"
                        />
                        <div>
                          <FieldLabel size="compact">Attempts</FieldLabel>
                          <TextInput
                            size="compact"
                            type="number"
                            value={String(retry.maxAttempts ?? 3)}
                            onChange={(v) =>
                              updateHook(idx, {
                                retry: {
                                  ...retry,
                                  maxAttempts: Math.max(1, Number(v) || 3),
                                },
                              })
                            }
                          />
                        </div>
                        <div>
                          <FieldLabel size="compact">Delay (ms)</FieldLabel>
                          <TextInput
                            size="compact"
                            type="number"
                            value={String(retry.delayMs ?? 2000)}
                            onChange={(v) =>
                              updateHook(idx, {
                                retry: {
                                  ...retry,
                                  delayMs: Math.max(0, Number(v) || 2000),
                                },
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </details>
                </div>
              </details>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowJson((v) => !v)}
          className="font-onest text-xs font-medium text-slate-600 hover:text-slate-800"
        >
          {showJson ? "Скрыть JSON" : "Показать JSON"}
        </button>
        {showJson ? (
          <div className="mt-2">
            <TextArea
              size="compact"
              value={value}
              rows={10}
              placeholder="[]"
              onChange={onChange}
            />
            <p className="mt-1 font-onest text-[11px] text-slate-500">
              При сохранении формы JSON сериализуется автоматически — в базе
              переносы строк выглядят как{" "}
              <code className="rounded bg-slate-100 px-1 py-px">\n</code>.
              Редактируйте шаблон в поле выше.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
